import { useMemo } from 'react';
import { useParty } from '../contexts/PartyContext';
import { useHostBranding } from './useHostBranding';

export interface PartyAdsAllowedResult {
  adsAllowed: boolean;
  loading: boolean; // true while evaluating premium state; returns false until known safe
}

/**
 * Centralized logic to determine if promotional ads should be shown during a party session.
 * Rules:
 * - If the party tier is 'premium': NO ADS
 * - Else if the host account is premium (active Party Pass or Pro): NO ADS
 * - Else: ADS ALLOWED
 *
 * Note: While host premium state is loading, this hook returns { adsAllowed:false, loading:true }
 * to avoid flashing ads briefly in paid sessions.
 */
export const usePartyAdsAllowed = (): PartyAdsAllowedResult => {
  const { currentParty } = useParty();
  const hostUserId = currentParty?.createdBy;
  const { isPremium: isPremiumHost, loading } = useHostBranding(hostUserId);

  const adsAllowed = useMemo(() => {
    if (!currentParty) return false; // don't show ads without a party context
    if (currentParty.tier === 'premium') return false;
    if (loading) return false; // be conservative until host premium is known
    if (isPremiumHost) return false;
    return true;
  }, [currentParty?.id, currentParty?.tier, isPremiumHost, loading]);

  return { adsAllowed, loading };
};
